import React, { useState, useEffect } from 'react';
import Dropdown from '../common/Dropdown';
import Button from '../common/Button';
import { Instrument, Resolution } from '../../types';
import './TradeModal.css';

interface OpenTrade {
  trade_id: number;
  tradingview_trade_id: string;
  instrument: string;
  timeframe: string;
  direction: string;
  entry_price: number;
  entry_time: string;
  size: number;
  entry_reason: string;
  status: string;
}

interface TradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'BUY' | 'SELL' | 'EXIT';
  instruments: Instrument[];
  resolutions: Resolution[];
  entryReasons: Array<{ value: string; label: string }>;
  exitReasons: Array<{ value: string; label: string }>;
  openTrades?: OpenTrade[];
  onSubmit: (data: {
    instrument: string;
    resolution: string;
    size: number;
    entryReason?: string;
    exitReason?: string;
    tradeId?: string;
  }) => void;
  loading: boolean;
}

const TradeModal: React.FC<TradeModalProps> = ({
  isOpen,
  onClose,
  type,
  instruments,
  resolutions,
  entryReasons,
  exitReasons,
  openTrades = [],
  onSubmit,
  loading
}) => {
  const [instrument, setInstrument] = useState('');
  const [resolution, setResolution] = useState('');
  const [size, setSize] = useState(1);
  const [entryReason, setEntryReason] = useState('');
  const [exitReason, setExitReason] = useState('');
  const [selectedTradeId, setSelectedTradeId] = useState('');
  const [selectedTrade, setSelectedTrade] = useState<OpenTrade | null>(null);

  useEffect(() => {
    if (type === 'EXIT' && selectedTradeId && openTrades.length > 0) {
      const trade = openTrades.find(t => t.tradingview_trade_id === selectedTradeId);
      setSelectedTrade(trade || null);
    } else {
      setSelectedTrade(null);
    }
  }, [selectedTradeId, openTrades, type]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit({
      instrument: type === 'EXIT' ? (selectedTrade?.instrument || '') : instrument,
      resolution: type === 'EXIT' ? (selectedTrade?.timeframe || '') : resolution,
      size: type === 'EXIT' ? (selectedTrade?.size || 0) : size,
      entryReason: type !== 'EXIT' ? entryReason : undefined,
      exitReason: type === 'EXIT' ? exitReason : undefined,
      tradeId: type === 'EXIT' ? selectedTradeId : undefined
    });
  };

  const isFormValid = () => {
    if (type === 'EXIT') {
      return exitReason && selectedTradeId;
    }
    return instrument && resolution && entryReason && size > 0;
  };

  return (
    <div className="trade-modal-overlay" onClick={onClose}>
      <div className="trade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="trade-modal-header">
          <h2>{type} Trade</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="trade-modal-body">
          {type !== 'EXIT' && (
            <>
              <Dropdown
                label="Instrument"
                value={instrument}
                onChange={setInstrument}
                options={instruments.map(i => ({
                  value: i.epic,
                  label: `${i.name} (${i.epic})`
                }))}
                placeholder="Select Instrument"
              />

              <Dropdown
                label="Resolution"
                value={resolution}
                onChange={setResolution}
                options={resolutions.map(r => ({
                  value: r.value,
                  label: r.label
                }))}
                placeholder="Select Resolution"
              />

              <div className="input-container">
                <label>Size</label>
                <input
                  type="number"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                  className="number-input"
                  min="0.1"
                  step="0.1"
                />
              </div>

              <Dropdown
                label="Entry Reason"
                value={entryReason}
                onChange={setEntryReason}
                options={entryReasons}
                placeholder="Select Entry Reason"
              />
            </>
          )}

          {type === 'EXIT' && (
            <>
              <Dropdown
                label="Select Trade to Close"
                value={selectedTradeId}
                onChange={setSelectedTradeId}
                options={openTrades.map(t => ({
                  value: t.tradingview_trade_id,
                  label: `${t.instrument} ${t.timeframe} - ${t.direction} @ $${t.entry_price.toFixed(4)} (Size: ${t.size})`
                }))}
                placeholder={openTrades.length === 0 ? "No open trades" : "Select Trade"}
              />

              {selectedTrade && (
                <div className="trade-details-card">
                  <h4>Trade Details</h4>
                  <div className="trade-detail-row">
                    <span className="detail-label">Instrument:</span>
                    <span className="detail-value">{selectedTrade.instrument}</span>
                  </div>
                  <div className="trade-detail-row">
                    <span className="detail-label">Timeframe:</span>
                    <span className="detail-value">{selectedTrade.timeframe}</span>
                  </div>
                  <div className="trade-detail-row">
                    <span className="detail-label">Direction:</span>
                    <span className={`detail-value direction-${selectedTrade.direction.toLowerCase()}`}>
                      {selectedTrade.direction}
                    </span>
                  </div>
                  <div className="trade-detail-row">
                    <span className="detail-label">Entry Price:</span>
                    <span className="detail-value">${selectedTrade.entry_price.toFixed(4)}</span>
                  </div>
                  <div className="trade-detail-row">
                    <span className="detail-label">Size:</span>
                    <span className="detail-value">{selectedTrade.size}</span>
                  </div>
                  <div className="trade-detail-row">
                    <span className="detail-label">Entry Time:</span>
                    <span className="detail-value">
                      {new Date(selectedTrade.entry_time).toLocaleString()}
                    </span>
                  </div>
                  <div className="trade-detail-row">
                    <span className="detail-label">Entry Reason:</span>
                    <span className="detail-value">{selectedTrade.entry_reason}</span>
                  </div>
                </div>
              )}

              <Dropdown
                label="Exit Reason"
                value={exitReason}
                onChange={setExitReason}
                options={exitReasons}
                placeholder="Select Exit Reason"
              />
            </>
          )}
        </div>

        <div className="trade-modal-footer">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="primary"
            loading={loading}
            disabled={!isFormValid() || loading}
          >
            {type} Trade
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TradeModal;
